import { Fetch } from '.';
import { Metadata } from '../types/Metadata';
import { View } from '../types/View';
import { Info } from '../types/Info';
import { ItemType } from '../enums/ItemType';

export type SearchResp = {
  groups: View.Group[];
  info: Info;
  swimlanes: View.Group[];
  data: Metadata[];
  regressionInfo: any;
};

export const searchData = (f: Fetch) => async (payload: View.SearchPayload) => {
  const { isRegressionMode, missingRequiredFilters } = payload;
  if (missingRequiredFilters?.length) {
    return { info: { missingRequiredFilters }, data: [] } as SearchResp;
  }
  let endpoint = isRegressionMode ? '/search/regression/data' : '/search/data';
  if (payload.view?.props?.type === 'Report') {
    endpoint = isRegressionMode
      ? '/search/regression/report'
      : '/search/report';
  }
  const r = await f.fetchJSON<SearchResp>(payload, endpoint);
  if (isRegressionMode && r.regressionInfo) {
    window.regressionInfo = r.regressionInfo;
  }
  return r;
};

export const getBoardCardCount =
  (f: Fetch) =>
  async (
    payload: View.SearchPayload & { cardCountOnly?: boolean }
  ): Promise<number> => {
    payload.includeCards = false;
    payload.cardCountOnly = true;
    const r = await searchGroups(f)(payload, null);
    return r?.info?.count;
  };

export const searchGroups =
  (f: Fetch) =>
  async (
    payload: View.SearchPayload,
    callback?: (resp: SearchResp, loadAdditional: boolean) => void
  ) => {
    const reqTimeInMs = new Date().getTime();
    const { isRegressionMode, missingRequiredFilters } = payload;
    const doSearch = async (endpoint: string, p?: View.SearchPayload) => {
      let r = {
        info: { missingRequiredFilters },
        groups: [],
        data: [],
        swimlanes: []
      } as SearchResp;
      if (!missingRequiredFilters?.length) {
        r = await f.fetchJSON<SearchResp>(p || payload, endpoint);
      }
      if (r?.groups?.length) {
        r.groups.forEach(g => {
          g.reqTimeInMs = reqTimeInMs;
        });
      }
      if (isRegressionMode && r?.regressionInfo) {
        window.regressionInfo = r.regressionInfo;
      }
      if (callback) callback(r, !!p);
      return r;
    };

    const path = isRegressionMode ? '/search/regression' : '/search';

    if (payload.includeCards) {
      return await doSearch(path + '/board/data');
    }
    if (payload.view?.props?.groupBy?.length > 1) {
      return await doSearch(path + '/board/groups');
    }
    const r = await doSearch(path + '/board/kanban');
    const groups = r?.groups?.filter(g => !g.cardRefIds);
    if (!!callback && groups?.length) {
      await doSearch('/search/board/kanban', { ...payload, groups });
    }
    return r;
  };

export type GetDataDetail = ((
  refId: string,
  type?: string
) => Promise<SearchResp>) &
  ((itemId: number) => Promise<SearchResp>);

export const getDataDetail =
  (f: Fetch): GetDataDetail =>
  async (p: string | number | boolean, type?: string) => {
    if (!p) {
      throw new Error('Either refId or itemId must be provided');
    }
    const q: string[] = [];
    const pType = typeof p;
    if (pType == 'number') {
      q.push(`itemId=${p}`);
    } else if (pType == 'string') {
      q.push(`refId=${p}`);
    }
    if (type) q.push(`type=${type}`);
    return await f.getJSON<SearchResp>(`/search/data/detail?${q.join('&')}`);
  };

export const getLastCreatedItem = (f: Fetch) => async (itemType: string) => {
  if (!itemType) {
    throw new Error('itemType must be provided');
  }
  const q: string[] = [];
  q.push(`lastCreatedItem=${itemType}`);
  return await f.getJSON<SearchResp>(`/search/data/detail?${q.join('&')}`);
};

export const searchLucene = (f: Fetch) => async (payload: string) =>
  f.fetchJSON<any>(payload, '/search/lucene');

export const searchEmployee =
  (f: Fetch) =>
  async (query: string): Promise<{ id: number; display: string }[]> => {
    const updatedQuery = query
      .split(' ')
      .map(p => p + '*')
      .join(' OR ');
    const searchResults = await searchLucene(f)(updatedQuery);
    return searchResults
      .filter(r => r.empCd && r.empName && r.empEmail)
      .map(r => {
        return { ...r, id: r.empCd, display: r.empEmail };
      });
  };
